import '@fortawesome/fontawesome-svg-core/styles.css'
import { Combobox, InputBase, useCombobox } from '@mantine/core'
import { IconStar } from 'components/icons/IconStar'
import { IconAndTitle } from 'components/SideNav/IconAndTitle/IconAndTitle'
import { translate } from 'i18n/i18n'
import { useMemo } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { layersNavigationMenuStore } from 'stores/layersNavigationMenuStore/layersNavigationMenuStore'
import { smallStore } from 'stores/smallStore/smallStore'
import type { TAvailablePresetId } from 'stores/userDetailsStore/helperTypes'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { setSelectedPreset } from 'utils/setSelectedPreset/setSelectedPreset'
import { sortByKey } from 'utils/sortByKey'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import './_utils/storeSubscriptions'
import { PresetDropdownContainer } from './PresetDropdownContainer/PresetDropdownContainer'

export const PresetLayers = () => {
  const selectedPreset = smallStore.useSelector((s) => s.selectedPreset)

  const allPresetsObject = userDetailsStore.useSelector((s) => ({
    ...s.availableSemiosDefinedPresets,
    ...s.availableUserDefinedPresets,
  }))

  const isWideScreen = useIsWideScreen()

  const combobox = useCombobox({
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const selectData = useMemo(() => {
    return Object.values(allPresetsObject)
      .sort(sortByKey('name'))
      .flatMap((preset) => (preset.ignored ? [] : { label: preset.name, value: preset.idHash }))
  }, [allPresetsObject])

  // we can hide the presets section when none are present (e.g. all are ignored)
  if (!selectData.length) return null

  const selectedLabel = selectData.find(({ value }) => value === selectedPreset)?.label ?? ''

  return (
    <div css={{ display: 'flex', flexDirection: 'column' }}>
      <IconAndTitle icon={<IconStar />} title={translate.phrases.banyanApp('Views')} />
      {/* TODO: replace this with MultiSelect when it's more complete */}
      {/* A Combobox, since Select has no slot for the "Manage Views" footer. The Select class names
          keep the app-wide select styles in mantine-overrides.css applying to it. */}
      <Combobox
        store={combobox}
        withinPortal={false}
        classNames={{
          dropdown: 'mantine-Select-dropdown',
          options: 'mantine-Select-options',
          option: 'mantine-Select-option',
        }}
        styles={{ dropdown: { width: 220, marginTop: -7 } }}
        onOptionSubmit={(idHash) => {
          combobox.closeDropdown()

          // Close layer menu on mobile view
          !isWideScreen && layersNavigationMenuStore.setState((s) => ({ ...s, menuCollapsed: true }))

          setSelectedPreset(idHash as TAvailablePresetId)
        }}
      >
        <Combobox.Target targetType="button">
          <InputBase
            css={{ margin: '0px 0px 10px 38px' }}
            classNames={{ input: 'mantine-Select-input', section: 'mantine-Select-section' }}
            styles={SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER}
            readOnly
            value={selectedLabel}
            placeholder={translate.phrases.banyanApp('Select a view')}
            rightSection={<Combobox.Chevron />}
            rightSectionPointerEvents="none"
            spellCheck="false"
            onClick={() => combobox.toggleDropdown()}
          />
        </Combobox.Target>
        <Combobox.Dropdown>
          <PresetDropdownContainer onManageViews={() => combobox.closeDropdown()}>
            <Combobox.Options>
              {selectData.map(({ label, value }) => {
                const checked = value === selectedPreset

                return (
                  <Combobox.Option
                    key={value}
                    value={value}
                    active={checked}
                    aria-selected={checked}
                    data-checked={checked || undefined}
                  >
                    {label}
                  </Combobox.Option>
                )
              })}
            </Combobox.Options>
          </PresetDropdownContainer>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  )
}
